import { useMemo } from 'react';

import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@helpcenter/constants';
import { getHelpCenterAri } from '@helpcenter/meta';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { useHubBasePath } from '@atlassian/company-hub-common/use-hub-base-path';

import { parseHelpCenterAri } from '../../../utils/ari-parser';
import type { HubCustomizeMenuSection } from '../hub-customize-menu';
import hubMessages from '../messages';

/**
 * The "Help center" group of the Customise menu — the site-wide entry points that
 * are the same on every page. Returns `null` for non-admins, which is what hides
 * the group (and, when it is the only group, the whole menu).
 *
 * Help center management is moving to the Jira side, so the same screens live at
 * two addresses while that ramps. `useHubBasePath` owns which one is live — it
 * reads the `jira_sidebar_show_helpcenter` experiment — and the two worlds do not
 * merely differ by prefix, so the base path also tells us which URL shapes to
 * build. Reading it from there keeps this off a second experiment lookup.
 */
export const useHelpCenterCustomizeSection = (
	canAdministerJIRA: boolean,
): HubCustomizeMenuSection | null => {
	di(getHelpCenterAri, useHubBasePath);
	const { formatMessage } = useIntl();
	const basePath = useHubBasePath();

	return useMemo(() => {
		if (!canAdministerJIRA) {
			return null;
		}

		const { helpCenterId } = parseHelpCenterAri(getHelpCenterAri());
		// Same check `hub-home` makes off this base path to pick its Rovo settings URL.
		const isInJira = basePath.startsWith('/jira');

		// Jira lists help centers at the base path itself; the help-center-hosted
		// screen lists them under /hubs.
		const managementUrl = isInJira ? basePath : `${basePath}/hubs`;

		// Both screens open their create flow from the same query param.
		const createHelpCenterUrl = `${managementUrl}?create=true`;

		const rovoAgentUrl = isInJira
			? `${basePath}/${helpCenterId}/settings?tab=rovo-agent`
			: `${basePath}/${helpCenterId}/rovo-agent`;

		// Deliberately not built from `basePath`: there is no Jira-side editor route,
		// so this stays on the help-center-hosted path in both worlds — the same thing
		// every `edit-hub` URL in `hub-home` does. It always opens the hub home page,
		// per the design, even when triggered from another page.
		const createCustomPageUrl = `${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/edit-hub?createPage=true`;

		return {
			heading: formatMessage(hubMessages.helpCenterSectionHeading),
			items: [
				{
					label: formatMessage(hubMessages.viewAllHelpCentersLabel),
					testId: 'view-all-help-centers-link',
					href: managementUrl,
					actionSubjectId: 'navbarViewAllHelpCenters',
				},
				{
					label: formatMessage(hubMessages.createHelpCenterLabel),
					testId: 'create-help-center-link',
					href: createHelpCenterUrl,
					actionSubjectId: 'navbarCreateHelpCenter',
				},
				{
					label: formatMessage(hubMessages.createCustomPageLabel),
					testId: 'create-custom-page-link',
					href: createCustomPageUrl,
					actionSubjectId: 'navbarCreateCustomPage',
				},
				{
					label: formatMessage(hubMessages.rovoAgentLabel),
					testId: 'rovo-agent-link',
					href: rovoAgentUrl,
					actionSubjectId: 'navbarRovoAgent',
				},
			],
		};
	}, [canAdministerJIRA, formatMessage, basePath]);
};
